import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';

import EmptyScreen from 'packages/ui/EmptyScreen';
import ScrollTable from 'components/global/CUK/ScrollTable';
import TableView, { useTable, IItem } from 'packages/ui/Table';
import TableSkeleton from 'components/global/CUK/Skeletons/TableSkeleton';

import { useSort } from '../store';
import useConnectedUsers from '../use-connected-users';
import COLUMNS_CONFIG from './config-columns';

interface TableProps {
  verbatim?: string;
}

const Table: React.FC<TableProps> = ({ verbatim }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [sort, setSort] = useSort();
  
  const apiUsers = useConnectedUsers({
    sortBy: sort.by,
    sortDirection: sort.direction,
    search: verbatim
  });

  const context = useMemo(() => ({
    t,
    items: apiUsers.items as unknown as IItem[]
  }), [t, apiUsers.items]);

  const columns = useMemo(() =>
    COLUMNS_CONFIG.map(col => ({
      ...col,
      value: typeof col.value === 'function'
        ? (item) => col.value(item, context)
        : col.value
    })),
    [context]
  );

  const apiTable = useTable({
    rowKey: 'id',
    loading: apiUsers.loading,
    loadingNext: apiUsers.loadingNext,
    items: apiUsers.items as unknown as IItem[],
    columns,
    onRowClick: (item) => {
      navigate(`/monitoring/connected-users/${item.id}`);
    }
  });

  if (!apiUsers.loading && !apiUsers.items.length) {
    return (
      <EmptyScreen icon="users">
        {t('supervision.connected_users.no_data')}
      </EmptyScreen>
    );
  }

  return (
    <ScrollTable
      fetchNextPage={apiUsers.fetchNextPage}
      shouldFetchNextPage={apiUsers.shouldFetchNextPage}
      collapse={Boolean(id)}
    >
      {
        apiUsers.loading
          ? <TableSkeleton />
          : apiUsers.error
            ? <Stack justifyContent={'center'} alignItems={'center'} sx={{ height: '100%' }}>
                <Typography component={'span'} sx={{ fontSize: 12 }} color={'error'}>
                  {t(`errors.${apiUsers.error}`)}
                </Typography>
              </Stack>
            : <TableView
                apiTable={apiTable}
                sort={{
                  ...sort,
                  direction: sort.direction as string,
                  by: sort.by as string,
                  onChange: name => {
                    const column = COLUMNS_CONFIG.find(column => column.name === name);
                    const sortBy = column?.sortBy || column?.name;
                    if (sortBy) {
                      setSort(sortBy);
                    }
                  }
                }}
              />
      }
    </ScrollTable>
  );
};

export default Table;